"use client";

import React, { useState, useEffect, useRef } from "react";
import {
  useVehicleTypesWithCount,
  useAddVehicle,
  useVehicleModels,
  useGetAllVehiclesTableData,
  useUpdateUserVehicleStatus,
  useDeleteVehicles,
  useAddVehicleWhileRegistration,
} from "@/hooks/useCommon";
import { useDebounce } from "@/hooks/useDebounce";
import { useSearchParams } from "next/navigation";
import { toast } from "react-toastify";
import {
  Save,
  Loader2,
  Plus,
  X,
  Check,
  Trash2,
  Edit,
  Upload,
  Circle,
} from "lucide-react";
import { useAuth } from "@/store/auth";
import BookingTable from "./common/Table";
import { useGetDriverList2, useGetUserDriverList } from "@/hooks/useCommon";
import { useSidebarToggle } from "@/store/common";

type VehicleSubRow = {
  id: number;
  vehicle_number: string;
  year_of_make: string;
  ownership_type: string;
  verified: boolean;
};

type VehicleRow = {
  id: number;
  vehicle_search: string;
  selectedVehicle: any | null;
  no_of_vehicles: string;
  subRows: VehicleSubRow[];
  showDropdown: boolean;
};

type VehicleModel = {
  id: number;
  vehicle_type_id: number;
  vehicle_type: string;
  vehicle_name: string;
  person_capacity: string;
  luggage_capacity: number;
  small_suitcase: number;
};

export default function VehicleFormSection() {
  const user = useAuth();
  const { SidebarData } = useSidebarToggle();

  const userId = user?.user?.id;

  const searchParams = useSearchParams();
  const isRedirectFlow = searchParams.get("redirect") === "true";

  // Initialize with no_of_vehicles as "0"
  const [vehicleRows, setVehicleRows] = useState<VehicleRow[]>([
    {
      id: 1,
      vehicle_search: "",
      selectedVehicle: null,
      no_of_vehicles: "0",
      subRows: [],
      showDropdown: false,
    },
  ]);

  const [searchTerms, setSearchTerms] = useState<{ [key: number]: string }>({});
  const [loadingIds, setLoadingIds] = useState<number[]>([]);
  const [selectedVehicles, setSelectedVehicles] = useState<number[]>([]);
  const [driverSearchTerm, setDriverSearchTerm] = useState("");
  const [selectedDriver, setSelectedDriver] = useState<any>(null);
  const [showDriverDropdown, setShowDriverDropdown] = useState(false);

  const { data: vehicleTypesData = [], isLoading: loadingTypes } =
    useVehicleTypesWithCount(userId);
  const { mutateAsync: addVehicleMutation, isPending: addingVehicle } =
    useAddVehicle();

  const {
    mutateAsync: addVehicleWhileRegistrationMutation,
    isPending: addVehicleRegistration,
  } = useAddVehicleWhileRegistration();

  // Use the same APIs from VehicleTableSection
  const { data: allModelsData = [] } = useGetAllVehiclesTableData();
  const { mutate: deleteVehiclesMutation, isPending: deletingVehicles } =
    useDeleteVehicles();
  const { mutate: updateStatusMutate } = useUpdateUserVehicleStatus();

  // Driver search for admin
  const debouncedDriverSearch = useDebounce(driverSearchTerm, 300);
  const [driverList, setDriverList] = useState<any[]>([]);
  const [loadingDrivers, setLoadingDrivers] = useState(false);
  const { mutateAsync: getDriverList } = useGetDriverList2();

  // Fetch drivers when search term changes (for admin)
  useEffect(() => {
    if (userId === 1 && debouncedDriverSearch.length >= 3) {
      setLoadingDrivers(true);
      getDriverList({
        body: {
          user_type_id: "3,4",
          company_id: 1,
        },
        query: {
          name: debouncedDriverSearch,
        },
      })
        .then((data) => {
          setDriverList(data || []);
        })
        .catch((error) => {
          console.error("Error fetching drivers:", error);
          setDriverList([]);
        })
        .finally(() => {
          setLoadingDrivers(false);
        });
    } else {
      setDriverList([]);
    }
  }, [debouncedDriverSearch, userId, getDriverList]);

  const totalInventory = vehicleTypesData.reduce(
    (sum: number, type: any) => sum + (type.count || 0),
    0
  );

  const vehiclesBeingAdded = vehicleRows.reduce(
    (sum, row) => sum + row.subRows.length,
    0
  );

  const totalVehiclesIncludingNew = totalInventory + vehiclesBeingAdded;

  //  New vehicle type rows always start with "0"
  const handleAddVehicleRow = () => {
    const newId = Date.now();
    setVehicleRows((prev) => [
      ...prev,
      {
        id: newId,
        vehicle_search: "",
        selectedVehicle: null,
        no_of_vehicles: "0", //  Always start with "0"
        subRows: [],
        showDropdown: false,
      },
    ]);
  };

  // console.log("driver list printing", driverList);

  const handleRemoveVehicleRow = (rowId: number) => {
    if (vehicleRows.length > 1) {
      setVehicleRows((prev) => prev.filter((row) => row.id !== rowId));
    }
  };

  const handleVehicleSearchChange = (rowId: number, value: string) => {
    setVehicleRows((prev) =>
      prev.map((row) =>
        row.id === rowId
          ? {
              ...row,
              vehicle_search: value,
              selectedVehicle: null,
              showDropdown: value.length >= 3,
            }
          : row
      )
    );

    setSearchTerms((prev) => ({ ...prev, [rowId]: value }));
  };

  const handleVehicleSelect = (rowId: number, vehicle: VehicleModel) => {
    setVehicleRows((prev) =>
      prev.map((row) =>
        row.id === rowId
          ? {
              ...row,
              selectedVehicle: vehicle,
              vehicle_search: vehicle.vehicle_name,
              showDropdown: false,
            }
          : row
      )
    );
  };

  const handleCloseDropdown = (rowId: number) => {
    setVehicleRows((prev) =>
      prev.map((row) =>
        row.id === rowId ? { ...row, showDropdown: false } : row
      )
    );
  };

  const handleNoOfVehiclesChange = (rowId: number, value: string) => {
    const count = parseInt(value) || 0;
    const clampedCount = Math.max(0, Math.min(20, count));

    setVehicleRows((prev) =>
      prev.map((row) => {
        if (row.id === rowId) {
          const currentCount = row.subRows.length;

          if (clampedCount > currentCount) {
            // Add new sub-rows
            const newSubRows = [...row.subRows];
            for (let i = currentCount; i < clampedCount; i++) {
              newSubRows.push({
                id: Date.now() + i,
                vehicle_number: "",
                year_of_make: "",
                ownership_type: "",
                verified: false,
              });
            }
            return { ...row, no_of_vehicles: value, subRows: newSubRows };
          } else if (clampedCount < currentCount) {
            // Remove extra sub-rows
            return {
              ...row,
              no_of_vehicles: value,
              subRows: row.subRows.slice(0, clampedCount),
            };
          } else if (clampedCount === 0) {
            // Clear all sub-rows when set to 0
            return {
              ...row,
              no_of_vehicles: value,
              subRows: [],
            };
          }
        }
        return { ...row, no_of_vehicles: value };
      })
    );
  };

  const handleSubRowChange = (
    rowId: number,
    subRowId: number,
    field: keyof VehicleSubRow,
    value: string
  ) => {
    setVehicleRows((prev) =>
      prev.map((row) => {
        if (row.id === rowId) {
          return {
            ...row,
            subRows: row.subRows.map((subRow) =>
              subRow.id === subRowId ? { ...subRow, [field]: value } : subRow
            ),
          };
        }
        return row;
      })
    );
  };

  const handleVerify = (rowId: number, subRowId: number) => {
    setVehicleRows((prev) =>
      prev.map((row) => {
        if (row.id === rowId) {
          return {
            ...row,
            subRows: row.subRows.map((subRow) =>
              subRow.id === subRowId ? { ...subRow, verified: true } : subRow
            ),
          };
        }
        return row;
      })
    );
    toast.success("Vehicle number verified!");
  };

  const handleAddVehicle = async (rowId: number, subRowId: number) => {
    const row = vehicleRows.find((r) => r.id === rowId);
    const subRow = row?.subRows.find((sr) => sr.id === subRowId);

    if (!row || !subRow || !row.selectedVehicle) {
      toast.warn("Please select a vehicle model and fill all fields");
      return;
    }

    if (
      !subRow.vehicle_number ||
      !subRow.year_of_make ||
      !subRow.ownership_type
    ) {
      toast.warn("Please fill all fields");
      return;
    }

    try {
      const normalizedOwnership =
        subRow.ownership_type && subRow.ownership_type.trim()
          ? subRow.ownership_type
          : "OWNED";

      const payload = {
        model_name: row.selectedVehicle.vehicle_name,
        vehicle_no: subRow.vehicle_number,
        year_of_make: subRow.year_of_make,
        vehicle_owner_type: normalizedOwnership,
        vehicle_type_id: row.selectedVehicle.vehicle_type_id,
        vehicle_type_name: row.selectedVehicle.vehicle_type,
        user_id:
          userId === 1 && selectedDriver ? selectedDriver.user_id : userId,
        total_fleet: totalVehiclesIncludingNew,
      };

      // const res = await addVehicleMutation(payload);

      const mutationFn = isRedirectFlow
        ? addVehicleWhileRegistrationMutation
        : addVehicleMutation;

      const res = await mutationFn(payload);

      if (res?.success) {
        toast.success("Vehicle added successfully!");

        if (isRedirectFlow && res?.data?.redirectTo) {
          window.location.href = res.data.redirectTo;
          return; // stop further execution
        }
        setVehicleRows(
          (prev) =>
            prev
              .map((r) => {
                if (r.id === rowId) {
                  const filteredSubRows = r.subRows.filter(
                    (sr) => sr.id !== subRowId
                  );
                  if (filteredSubRows.length === 0) {
                    // If no sub-rows left, remove the entire row
                    return null;
                  }
                  return {
                    ...r,
                    subRows: filteredSubRows,
                    no_of_vehicles: filteredSubRows.length.toString(),
                  };
                }
                return r;
              })
              .filter(Boolean) as VehicleRow[]
        );
      } else {
        toast.error(res?.message || "Failed to add vehicle");
      }
    } catch (error: any) {
      console.error("Add vehicle error:", error);
      toast.error(error?.response?.data?.message || "Something went wrong");
    }
  };

  const handleSaveAll = async () => {
    const allVehicles = vehicleRows.flatMap((row) =>
      row.subRows.map((subRow) => ({ row, subRow }))
    );

    // const invalidVehicles = allVehicles.filter(
    //   ({ row, subRow }) =>
    //     !row.selectedVehicle ||
    //     !subRow.vehicle_number ||
    //     !subRow.year_of_make ||
    //     !subRow.ownership_type
    // );

    // if (invalidVehicles.length > 0) {
    //   toast.warn(
    //     `Please fill all fields for ${invalidVehicles.length} vehicle(s)`
    //   );
    //   return;
    // }

    try {
      for (const { row, subRow } of allVehicles) {
        const normalizedOwnership =
          subRow.ownership_type && subRow.ownership_type.trim()
            ? subRow.ownership_type
            : "OWNED";

        const payload = {
          model_name: row.selectedVehicle!.vehicle_name,
          vehicle_no: subRow.vehicle_number,
          year_of_make: subRow.year_of_make,
          vehicle_owner_type: normalizedOwnership,
          vehicle_type_id: row.selectedVehicle!.vehicle_type_id,
          vehicle_type_name: row.selectedVehicle!.vehicle_type,
          user_id:
            userId === 1 && selectedDriver ? selectedDriver.user_id : userId,
        };
        const mutationFn = isRedirectFlow
          ? addVehicleWhileRegistrationMutation
          : addVehicleMutation;

        await mutationFn(payload);
      }

      toast.success(`${allVehicles.length} vehicles saved successfully!`);

      // Reset all rows to initial state
      setVehicleRows([
        {
          id: Date.now(),
          vehicle_search: "",
          selectedVehicle: null,
          no_of_vehicles: "0", // Reset to "0"
          subRows: [], // Empty array
          showDropdown: false,
        },
      ]);
    } catch (error) {
      console.error("Save all error:", error);
      toast.error("Error while saving vehicles");
    }
  };

  // useEffect(
  // () => console.log("selected driver", selectedDriver),
  // [selectedDriver]
  // );

  // Functions from VehicleTableSection
  const verifyVehicle = (id: number) => {
    if (loadingIds.includes(id)) return;
    setLoadingIds((prev) => [...prev, id]);
    setTimeout(() => {
      setLoadingIds((prev) => prev.filter((i) => i !== id));
      toast.success("Vehicle verified successfully!");
    }, 800);
  };

  const handleVehicleSelectChange = (vehicleId: number, checked: boolean) => {
    if (checked) {
      setSelectedVehicles((prev) => [...prev, vehicleId]);
    } else {
      setSelectedVehicles((prev) => prev.filter((id) => id !== vehicleId));
    }
  };

  const handleSelectAllChange = (checked: boolean) => {
    if (checked) {
      setSelectedVehicles(allModelsData.map((vehicle: any) => vehicle.id));
    } else {
      setSelectedVehicles([]);
    }
  };

  const handleDeleteVehicle = (vehicleId: number) => {
    if (window.confirm("Are you sure you want to delete this vehicle?")) {
      deleteVehiclesMutation([vehicleId]);
    }
  };

  const handleDeleteMultiple = () => {
    if (selectedVehicles.length === 0) {
      toast.warn("Please select at least one vehicle to delete.");
      return;
    }

    if (
      window.confirm(
        `Are you sure you want to delete ${selectedVehicles.length} vehicle(s)?`
      )
    ) {
      deleteVehiclesMutation(selectedVehicles);
      setSelectedVehicles([]);
    }
  };

  const onStatusChanged = (status_id: number, row_id?: any) => {
    let type_ids = [row_id];
    if (!row_id) {
      if (selectedVehicles.length === 0 && row_id === undefined) {
        toast.error("No rows selected");
        return;
      }

      const selectedRoutes = allModelsData.filter((row: any) =>
        selectedVehicles.includes(row.id)
      );
      type_ids = selectedRoutes.map((row: any) => row.id);
    }

    updateStatusMutate(
      {
        status: status_id,
        id: type_ids,
      },
      {
        onSuccess: (data) => {
          if (data.status == "success") {
            toast.success(
              status_id == 2 ? "Data deleted successfully" : data?.message
            );
          }
          setSelectedVehicles([]);
        },
        onError: (error) => {
          toast.error("Something went wrong");
        },
      }
    );
  };

  // Columns definition for BookingTable
  const columns = [
    {
      key: "checkbox",
      header: (
        <input
          type="checkbox"
          className="w-4 h-4 accent-blue-600"
          onChange={(e) => handleSelectAllChange(e.target.checked)}
          checked={
            selectedVehicles.length > 0 &&
            selectedVehicles.length === allModelsData.length
          }
        />
      ),
      render: (_: any, row: any) => (
        <input
          type="checkbox"
          className="w-4 h-4 accent-blue-600"
          checked={selectedVehicles.includes(row.id)}
          onChange={(e) => handleVehicleSelectChange(row.id, e.target.checked)}
        />
      ),
    },
    {
      key: "sno",
      header: "S.No",
      sortable: true,
      render: (val: number) => val ?? "—",
    },
    {
      key: "vendor_details",
      header: "Vendor Details",
      sortable: true,
      render: (_: any, row: any) => (
        <div className="text-xs text-gray-700">
          <div className="font-medium">{row.vendor_name || "—"}</div>
          <div className="text-xs text-gray-500">
            {row.vendor_email ? row.vendor_email : "No email"}
          </div>
          <div className="text-xs text-gray-500">
            {row.vendor_city ? row.vendor_city : "No city"}
          </div>
          <div className="text-xs text-gray-500">
            {row.vendor_mobile ? `+91 ${row.vendor_mobile}` : "No mobile"}
          </div>
        </div>
      ),
    },
    {
      key: "model",
      header: "Year of Make",
      sortable: true,
      render: (val: any) => (val === null ? "—" : val),
    },
    {
      key: "vehicle_type_name",
      header: "Vehicle Type",
      sortable: true,
    },
    {
      key: "vehicle_name",
      header: "Vehicle Name",
      sortable: true,
    },
    {
      key: "vehicle_no",
      header: "Vehicle Number",
      sortable: true,
    },
    {
      key: "pending_documents",
      header: "Pending Documents",
      sortable: true,
      render: (val: string) =>
        val ? (
          <div className="flex flex-wrap gap-1 max-w[180px]">
            {val.split(", ").map((doc, idx) => (
              <span
                key={idx}
                className="bg-yellow-50 text-yellow-700 border border-yellow-200 text-xs px-2 py-1 rounded-md"
              >
                {doc}
              </span>
            ))}
          </div>
        ) : (
          <span className="text-gray-400 text-xs italic">
            No pending documents
          </span>
        ),
    },
    {
      key: "aircondition",
      header: "AC / Non-AC",
      sortable: true,
      render: (val: string) => (
        <span
          className={`px-2 py-1 rounded-md text-xs font-medium ${
            val === "AC"
              ? "bg-green-50 text-green-700 border border-green-200"
              : "bg-gray-50 text-gray-700 border border-gray-200"
          }`}
        >
          {val || "—"}
        </span>
      ),
    },
    {
      key: "status",
      header: "Status",
      sortable: true,
      render: (_: string, row: any) => (
        <div className="flex items-center gap-2">
          {row.status == 1 ? (
            <span className="h-2.5 w-2.5 rounded-full bg-green-500" />
          ) : (
            <span className="h-2.5 w-2.5 rounded-full bg-red-800" />
          )}
        </div>
      ),
    },
    {
      key: "action",
      header: "Action",
      sortable: false,
      render: (_: any, row: any) => {
        const isVerified = row.verified;
        const isLoading = loadingIds.includes(row.id);
        return (
          <div className="flex items-center gap-2">
            {isVerified ? (
              <button
                className="inline-flex items-center gap-2 px-3 py-1 text-[11px] rounded-md bg-gray-100 border border-gray-200 text-gray-600 cursor-not-allowed"
                disabled
              >
                <Check className="w-3 h-3 text-green-600" />
                Verified
              </button>
            ) : (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  verifyVehicle(row.id);
                }}
                className="inline-flex items-center gap-2 px-3 py-1 text-[11px] rounded-md bg-green-50 hover:bg-green-100 border border-green-200 text-green-700"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-3 h-3 animate-spin" />
                    Verifying...
                  </>
                ) : (
                  <>
                    <Circle className="w-3 h-3" />
                    Verify
                  </>
                )}
              </button>
            )}

            {/* Edit, Upload and Delete Actions */}
            <div className="flex items-center gap-1 ml-2">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  // onEditHandler(row);
                }}
                className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                title="Edit Vehicle"
              >
                <Edit className="w-4 h-4" />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  // handleUploadDocument(row);
                }}
                className="p-1.5 text-green-600 hover:bg-green-50 rounded-md transition-colors"
                title="Upload Documents"
              >
                <Upload className="w-4 h-4" />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteVehicle(row.id);
                }}
                className="p-1.5 text-red-600 hover:bg-red-50 rounded-md transition-colors"
                title="Delete Vehicle"
                disabled={deletingVehicles}
              >
                {deletingVehicles ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Trash2 className="w-4 h-4" />
                )}
              </button>
            </div>
          </div>
        );
      },
    },
  ];

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest(".vehicle-search-container")) {
        setVehicleRows((prev) =>
          prev.map((row) => ({ ...row, showDropdown: false }))
        );
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div className="space-y-6">
      {/* Top Section - Form and Overview */}
      <div className="flex gap-6">
        {/* Add Vehicle Form */}
        <div className="w-2/3 bg-white p-6 rounded-xl shadow-sm border border-gray-200">
          <h2 className="text-xl font-semibold mb-4">Add Vehicle</h2>

          {/* Total Inventory Display - Compact single row */}
          <div className="mb-6 flex items-center gap-4">
            <div className="flex items-center gap-2">
              <h3 className="text-lg font-medium text-gray-800">
                Total Inventory:
              </h3>
              <span className="text-2xl font-bold text-gray-900">
                {totalInventory}
              </span>
            </div>

            {/* Driver Search for Admin */}
            {userId === 1 && (
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium text-gray-600">
                  Assign to Driver:
                </span>
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search driver..."
                    className="w-48 border border-gray-300 rounded-md px-3 py-2 text-xs"
                    value={driverSearchTerm}
                    onChange={(e) => {
                      setDriverSearchTerm(e.target.value);
                      setShowDriverDropdown(e.target.value.length >= 3);
                    }}
                    onFocus={() => {
                      if (driverSearchTerm.length >= 3) {
                        setShowDriverDropdown(true);
                      }
                    }}
                  />
                  {showDriverDropdown && (
                    <DriverSearchResults
                      searchTerm={debouncedDriverSearch}
                      drivers={driverList}
                      loading={loadingDrivers}
                      onSelect={(driver) => {
                        setSelectedDriver(driver);
                        setDriverSearchTerm(`${driver.name} ${driver.email}`);
                        setShowDriverDropdown(false);
                      }}
                      onClose={() => setShowDriverDropdown(false)}
                    />
                  )}
                </div>
                {selectedDriver && (
                  <button
                    onClick={() => {
                      setSelectedDriver(null);
                      setDriverSearchTerm("");
                    }}
                    className="text-xs text-red-500 hover:text-red-700"
                    title="Clear driver selection"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Vehicle Rows */}
          {vehicleRows.map((row) => (
            <div
              key={row.id}
              className="mb-6 p-4 border border-gray-200 rounded-lg bg-white"
            >
              {/* Row Header */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <h4 className="font-medium text-gray-700">Vehicle Type</h4>
                  {vehicleRows.length > 1 && (
                    <button
                      onClick={() => handleRemoveVehicleRow(row.id)}
                      className="text-gray-400 hover:text-red-500"
                      title="Remove this vehicle type"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>

              {/* Vehicle Search and No. of Vehicles in single row */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                {/* Vehicle Search */}
                <div className="vehicle-search-container relative">
                  <label className="block text-xs font-medium text-gray-700 mb-2">
                    Vehicle Model
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Search vehicle model..."
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-xs"
                      value={row.vehicle_search}
                      onChange={(e) =>
                        handleVehicleSearchChange(row.id, e.target.value)
                      }
                      onFocus={() => {
                        if (row.vehicle_search.length >= 3) {
                          setVehicleRows((prev) =>
                            prev.map((r) =>
                              r.id === row.id ? { ...r, showDropdown: true } : r
                            )
                          );
                        }
                      }}
                    />
                    {row.showDropdown && (
                      <SearchResults
                        searchTerm={searchTerms[row.id] || ""}
                        onSelect={(vehicle) =>
                          handleVehicleSelect(row.id, vehicle)
                        }
                        onClose={() => handleCloseDropdown(row.id)}
                      />
                    )}
                  </div>

                  {row.selectedVehicle && (
                    <div className="mt-2 p-2 bg-gray-50 border border-gray-200 rounded-md">
                      <div className="flex items-center justify-between">
                        <div>
                          <span className="font-medium">
                            {row.selectedVehicle.vehicle_name}
                          </span>
                          <span className="text-xs text-gray-500 ml-2">
                            (Type: {row.selectedVehicle.vehicle_type})
                          </span>
                        </div>
                        <Check className="w-4 h-4 text-green-500" />
                      </div>
                    </div>
                  )}
                </div>

                {/* No. of Vehicles */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-2">
                    No. of Vehicles
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="number"
                      min="0"
                      max="20"
                      className="flex-1 border border-gray-300 rounded-md px-3 py-2 text-xs"
                      value={row.no_of_vehicles}
                      onChange={(e) =>
                        handleNoOfVehiclesChange(row.id, e.target.value)
                      }
                      placeholder="Enter number"
                    />
                    <button
                      onClick={() => {
                        const currentCount = parseInt(row.no_of_vehicles) || 0;
                        handleNoOfVehiclesChange(
                          row.id,
                          (currentCount + 1).toString()
                        );
                      }}
                      className="px-3 py-2 bg-blue-500 hover:bg-blue-600 text-white text-xs rounded-md whitespace-nowrap"
                      title="Add one more row"
                    >
                      Add Row
                    </button>
                  </div>
                </div>
              </div>

              {/* Sub-rows for each vehicle - Only show if no_of_vehicles > 0 */}
              {parseInt(row.no_of_vehicles) > 0 && (
                <div className="space-y-3">
                  {row.subRows.map((subRow, index) => (
                    <div
                      key={subRow.id}
                      className="p-3 border border-gray-200 rounded-md"
                    >
                      {/* Single row layout for vehicle details */}
                      <div className="flex items-end gap-3">
                        {/* Vehicle Number with Verify */}
                        <div className="flex-1">
                          <label className="block text-xs font-medium text-gray-600 mb-1">
                            Vehicle Number
                          </label>
                          <div className="flex gap-1">
                            <input
                              type="text"
                              placeholder="Number"
                              className="border border-gray-300 rounded-md px-2 py-2 text-xs w-full"
                              value={subRow.vehicle_number}
                              onChange={(e) =>
                                handleSubRowChange(
                                  row.id,
                                  subRow.id,
                                  "vehicle_number",
                                  e.target.value
                                )
                              }
                            />
                          </div>
                          {subRow.verified && (
                            <div className="mt-1 text-xs text-green-600 flex items-center gap-1">
                              <Check className="w-3 h-3" />
                              Verified
                            </div>
                          )}
                        </div>

                        {/* Year of Make */}
                        <div className="flex-1">
                          <label className="block text-xs font-medium text-gray-600 mb-1">
                            Year
                          </label>
                          <input
                            type="text"
                            placeholder="Year"
                            className="border border-gray-300 rounded-md px-2 py-2 text-xs w-full"
                            value={subRow.year_of_make}
                            onChange={(e) =>
                              handleSubRowChange(
                                row.id,
                                subRow.id,
                                "year_of_make",
                                e.target.value
                              )
                            }
                          />
                        </div>

                        {/* Ownership Type */}
                        <div className="flex-1">
                          <label className="block text-xs font-medium text-gray-600 mb-1">
                            Ownership
                          </label>
                          <select
                            className="border border-gray-300 rounded-md px-2 py-2 text-xs w-full"
                            value={subRow.ownership_type}
                            onChange={(e) =>
                              handleSubRowChange(
                                row.id,
                                subRow.id,
                                "ownership_type",
                                e.target.value
                              )
                            }
                          >
                            <option value="">Select</option>
                            <option value="OWNED">Owned</option>
                            <option value="ATTACHED">Attached</option>
                          </select>
                        </div>

                        {/* Add Vehicle Button - Green and small */}
                        <div>
                          <button
                            onClick={() => handleAddVehicle(row.id, subRow.id)}
                            disabled={
                              addingVehicle ||
                              !subRow.vehicle_number ||
                              !subRow.year_of_make ||
                              !subRow.ownership_type ||
                              !row.selectedVehicle
                            }
                            className="flex items-center gap-1 bg-green-600 hover:bg-green-700 text-white text-xs px-3 py-2 rounded-md disabled:opacity-50 whitespace-nowrap"
                          >
                            {addingVehicle ? (
                              <Loader2 className="w-3 h-3 animate-spin" />
                            ) : (
                              "Add"
                            )}
                          </button>
                        </div>
                        <button
                          onClick={() => handleVerify(row.id, subRow.id)}
                          disabled={!subRow.vehicle_number}
                          className="flex items-center gap-1 bg-[#55BD42] hover:bg-green-700 text-white text-xs px-3 py-2 rounded-md disabled:opacity-50 whitespace-nowrap"
                        >
                          Verify
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}

          {/* Action buttons on right side */}
          <div className="flex justify-end items-center gap-3 mt-6">
            {/* Add More Vehicle Type Button */}
            <button
              onClick={handleAddVehicleRow}
              className="flex items-center gap-2 text-blue-600 hover:text-blue-700 text-xs font-medium"
            >
              <Plus className="w-4 h-4" />
              Add More Vehicle Type
            </button>

            {/* Save All Button */}
            <button
              onClick={handleSaveAll}
              disabled={addingVehicle}
              className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white text-xs px-4 py-2 rounded-md disabled:opacity-50"
            >
              {addingVehicle ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              Save All (
              {vehicleRows.reduce((sum, row) => sum + row.subRows.length, 0)})
            </button>
          </div>
        </div>

        {/* Right Overview */}
        <div className="w-1/3 bg-white p-6 rounded-xl shadow-sm border border-gray-200">
          <h3 className="text-lg font-semibold mb-4">Vehicle Type Overview</h3>
          {loadingTypes ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
            </div>
          ) : vehicleTypesData.length === 0 ? (
            <p className="text-gray-500 text-center py-8">
              No vehicle types found
            </p>
          ) : (
            <ul className="space-y-1 max-h-[400px] overflow-auto pr-2">
              {vehicleTypesData.map((t: any) => (
                <li
                  key={t.id}
                  className="flex items-center justify-between px-2 border-b border-gray-100 last:border-b-0 hover:bg-gray-50 rounded"
                >
                  <span className="text-xs font-medium">{t.vehicle_type}</span>
                  <span className="text-xs font-bold text-gray-800 bg-gray-100 px-3 py-1 rounded-full">
                    {t.count || 0}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Vehicle List Table using BookingTable */}
      <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">Your Vehicle List</h3>
          {selectedVehicles.length > 0 && (
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-600">
                {selectedVehicles.length} vehicle(s) selected
              </span>
              <button
                onClick={handleDeleteMultiple}
                disabled={deletingVehicles}
                className="flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white text-xs px-3 py-1.5 rounded-md"
              >
                {deletingVehicles ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Trash2 className="w-4 h-4" />
                )}
                Delete Selected
              </button>
            </div>
          )}
        </div>

        <div
          className={`overflow-x-auto bg-white rounded-lg shadow-lg mx-auto max-w-[90vw] md:max-w-[80vw] mb-4 pb-3 ${
            !SidebarData?.isCollapsed ? "lg:max-w-[75vw]" : "lg:max-w-[90vw]"
          }`}
        >
          <BookingTable
            data={allModelsData}
            columns={columns}
            itemsPerPage={10}
            searchable={true}
            filterable={true}
            sortable={true}
          />
        </div>
      </div>
    </div>
  );
}

// Search Results Component (unchanged)
interface SearchResultsProps {
  searchTerm: string;
  onSelect: (vehicle: VehicleModel) => void;
  onClose: () => void;
}

function SearchResults({ searchTerm, onSelect, onClose }: SearchResultsProps) {
  const handleSelect = (vehicle: VehicleModel) => {
    onSelect(vehicle);
  };
  const debouncedSearch = useDebounce(searchTerm, 500);

  const { data: searchResults = [], isLoading: searching } = useVehicleModels(
    debouncedSearch.length >= 3 ? debouncedSearch : undefined
  );

  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        onClose();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [onClose]);

  if (!searchTerm || searchTerm.length < 3) return null;

  return (
    <div
      ref={dropdownRef}
      className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-48 overflow-auto"
    >
      {searching ? (
        <div className="p-2 text-center text-xs text-gray-500">
          <Loader2 className="w-3 h-3 animate-spin mx-auto mb-1" />
          Searching...
        </div>
      ) : searchResults.length === 0 ? (
        <div className="p-2 text-center text-xs text-gray-500">
          No vehicles found
        </div>
      ) : (
        <ul>
          {searchResults.slice(0, 5).map((vehicle: VehicleModel) => (
            <li
              key={vehicle.id}
              onClick={() => {
                onSelect(vehicle);
                onClose();
              }}
              className="p-2 hover:bg-gray-100 cursor-pointer border-b border-gray-100 last:border-b-0 text-xs"
            >
              <div className="font-medium">{vehicle.vehicle_name}</div>
              <div className="text-xs text-gray-500 truncate">
                {vehicle.vehicle_type || "N/A"}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// Driver Search Results Component for Admin
interface DriverSearchResultsProps {
  searchTerm: string;
  drivers: any[];
  loading: boolean;
  onSelect: (driver: any) => void;
  onClose: () => void;
}

function DriverSearchResults({
  searchTerm,
  drivers,
  loading,
  onSelect,
  onClose,
}: DriverSearchResultsProps) {
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        onClose();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [onClose]);

  if (!searchTerm || searchTerm.length < 3) return null;

  return (
    <div
      ref={dropdownRef}
      className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-48 overflow-auto"
    >
      {loading ? (
        <div className="p-2 text-center text-xs text-gray-500">
          <Loader2 className="w-3 h-3 animate-spin mx-auto mb-1" />
          Searching drivers...
        </div>
      ) : drivers.length === 0 ? (
        <div className="p-2 text-center text-xs text-gray-500">
          No drivers found
        </div>
      ) : (
        <ul>
          {drivers.slice(0, 5).map((driver: any) => (
            <li
              key={driver.id}
              onClick={() => {
                onSelect(driver);
                onClose();
              }}
              className="p-2 hover:bg-gray-100 cursor-pointer border-b border-gray-100 last:border-b-0 text-xs"
            >
              <div className="font-medium">{`${driver.name} ${driver.vendor_name}`}</div>
              <div className="text-xs text-gray-600">{driver.mobile}</div>
              <div className="text-xs text-gray-500">
                {driver.email || "No email"}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
